import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNoIndex } from "../../hooks/usePageSeo";
import {
  getConfirmationLinkPublic,
  verifyConfirmationLink,
  updateConfirmationResponse,
} from "../../firebase";
import {
  CalendarCheck,
  CalendarX,
  Clock,
  MapPin,
  Phone,
  ShieldCheck,
  Warning,
  CheckCircle,
  XCircle,
  Timer,
  VideoCamera,
  CalendarBlank,
} from "@phosphor-icons/react";
import ContentLoader from "../../components/shared/ContentLoader";
import wazeIcon from "../../assets/waze_icon.png";
import PublicClinicHeader from "../../components/public/PublicClinicHeader";
import {
  getConfirmStrings,
  getConfirmLocale,
} from "./confirmStrings";
import "./ConfirmAppointmentPage.css";

// Stages of the confirmation flow
const STAGES = {
  LOADING: "loading",
  VERIFY: "verify",
  DETAILS: "details",
  CONFIRMING: "confirming",
  SUCCESS: "success",
  ERROR: "error",
};

function ConfirmAppointmentPage() {
  const { linkId } = useParams();
  useNoIndex();
  const [stage, setStage] = useState(STAGES.LOADING);
  const [linkData, setLinkData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [confirmationType, setConfirmationType] = useState(null); // 'confirm' or 'decline'
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [response, setResponse] = useState(null); // Final response after submission
  const [cancellationBlocked, setCancellationBlocked] = useState(false);
  // Page language, told to us by the server with the link. Hebrew until the
  // link loads — the only thing rendered before then is a spinner.
  const [language, setLanguage] = useState("he");

  const t = getConfirmStrings(language);

  // Props for the single page root, so the LTR flip lives in one place. Spread
  // onto a plain <div> rather than wrapped in a component — a component defined
  // inside the render would be a new type every render, remounting the subtree
  // and stealing focus from the verification input on every keystroke.
  // `dir` is set explicitly in both directions; the RTL value matches what the
  // subtree inherits today, so a Hebrew patient's page is unchanged.
  const pageProps = {
    className: `capg-page${language === "en" ? " capg-ltr" : ""}`,
    dir: language === "en" ? "ltr" : "rtl",
    lang: language,
  };

  // Load link data on mount
  useEffect(() => {
    const loadLink = async () => {
      try {
        const data = await getConfirmationLinkPublic(linkId);

        if (!data) {
          setErrorMessage(getConfirmStrings("he").linkNotFound);
          setStage(STAGES.ERROR);
          return;
        }

        // Adopt the patient's language before rendering anything with text.
        const pageLanguage = data.link?.language === "en" ? "en" : "he";
        setLanguage(pageLanguage);
        const strings = getConfirmStrings(pageLanguage);

        if (!data.link.isActive) {
          setErrorMessage(strings.linkInactive);
          setStage(STAGES.ERROR);
          return;
        }

        // Check if already responded
        if (data.link.response) {
          setLinkData(data);
          setResponse(data.link.response);
          setStage(STAGES.SUCCESS);
          return;
        }

        setLinkData(data);
        setStage(STAGES.VERIFY);
      } catch (error) {
        console.error("[CK-6001] Error loading confirmation link:", {
          errorCode: "CK-6001",
          errorMessage: error.message,
          linkId,
          timestamp: new Date().toISOString(),
        });
        setErrorMessage(getConfirmStrings("he").loadError);
        setStage(STAGES.ERROR);
      }
    };

    loadLink();
  }, [linkId]);

  // Handle verification
  const handleVerify = async () => {
    if (verificationCode.length !== 4) {
      setVerificationError(t.enterFourDigits);
      return;
    }

    setVerificationError("");

    try {
      const result = await verifyConfirmationLink(linkId, verificationCode);

      if (!result.success) {
        if (result.error === "INVALID_CODE") {
          setVerificationError(t.invalidCode);
        } else if (result.error === "TOO_MANY_ATTEMPTS") {
          setVerificationError(t.tooManyAttempts);
        } else if (result.error === "LINK_INACTIVE") {
          setVerificationError(t.linkInactiveVerify);
        } else {
          setVerificationError(t.verifyGenericError);
        }
        return;
      }

      setStage(STAGES.DETAILS);
    } catch (error) {
      console.error("[CK-6002] Verification error:", {
        errorCode: "CK-6002",
        errorMessage: error.message,
        linkId,
        timestamp: new Date().toISOString(),
      });
      setVerificationError(t.verifyGenericError);
    }
  };

  // Check if cancellation is blocked by time restriction
  const isCancellationBlocked = () => {
    if (!linkData?.link?.cancelRestrictionHoursBefore) return false;
    const restrictionHours = linkData.link.cancelRestrictionHoursBefore;
    const appointmentDate = linkData.link.appointmentDate;
    const appointmentTime = linkData.link.appointmentTime;
    if (!appointmentDate || !appointmentTime) return false;

    const [hours, minutes] = appointmentTime.split(":");
    const appointmentStart = new Date(appointmentDate);
    appointmentStart.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    const hoursUntilAppointment = (appointmentStart.getTime() - Date.now()) / (1000 * 60 * 60);
    return hoursUntilAppointment < restrictionHours;
  };

  // Handle confirmation/decline
  const handleResponse = async (responseType) => {
    setConfirmationType(responseType);

    // For confirmation - submit directly without popup
    if (responseType === "confirm") {
      setStage(STAGES.CONFIRMING);

      const responseValue = "confirmed";

      try {
        // Get device info
        const deviceInfo = {
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
          timestamp: new Date().toISOString(),
        };

        const result = await updateConfirmationResponse(
          linkId,
          responseValue,
          deviceInfo
        );

        if (result.success) {
          setResponse(responseValue);
          setStage(STAGES.SUCCESS);
        } else {
          console.error(
            "[CK-6023] updateConfirmationResponse returned failure:",
            {
              error: result.error,
              errorMessage: result.errorMessage,
              appointmentId: result.appointmentId,
            }
          );
          if (result.error === "APPOINTMENT_NOT_FOUND") {
            setErrorMessage(t.appointmentNotFound);
          } else if (result.error === "APPOINTMENT_UPDATE_FAILED") {
            setErrorMessage(t.appointmentUpdateFailed);
          } else {
            setErrorMessage(t.saveResponseError);
          }
          setStage(STAGES.ERROR);
        }
      } catch (error) {
        console.error("[CK-6003] Error submitting confirmation response:", {
          errorCode: "CK-6003",
          errorMessage: error.message,
          linkId,
          responseType,
          responseValue,
          timestamp: new Date().toISOString(),
        });
        setErrorMessage(t.saveResponseError);
        setStage(STAGES.ERROR);
      }
      return;
    }

    // For decline - check time restriction first
    if (isCancellationBlocked()) {
      setCancellationBlocked(true);
      return;
    }
    setShowConfirmPopup(true);
  };

  // Submit the response
  const submitResponse = async () => {
    setShowConfirmPopup(false);
    setStage(STAGES.CONFIRMING);

    const responseValue =
      confirmationType === "confirm" ? "confirmed" : "declined";

    try {
      // Get device info
      const deviceInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        timestamp: new Date().toISOString(),
      };

      const result = await updateConfirmationResponse(
        linkId,
        responseValue,
        deviceInfo
      );

      if (result.success) {
        setResponse(responseValue);
        setStage(STAGES.SUCCESS);
      } else {
        console.error(
          "[CK-6023] updateConfirmationResponse returned failure:",
          {
            error: result.error,
            errorMessage: result.errorMessage,
            appointmentId: result.appointmentId,
          }
        );
        if (result.error === "CANCELLATION_TOO_LATE") {
          setCancellationBlocked(true);
          setStage(STAGES.DETAILS);
        } else if (result.error === "APPOINTMENT_NOT_FOUND") {
          setErrorMessage(t.appointmentNotFound);
          setStage(STAGES.ERROR);
        } else if (result.error === "APPOINTMENT_UPDATE_FAILED") {
          setErrorMessage(t.appointmentUpdateFailed);
          setStage(STAGES.ERROR);
        } else {
          setErrorMessage(t.saveResponseError);
          setStage(STAGES.ERROR);
        }
      }
    } catch (error) {
      console.error("[CK-6003] Error submitting confirmation response:", {
        errorCode: "CK-6003",
        errorMessage: error.message,
        linkId,
        responseType: confirmationType,
        responseValue,
        timestamp: new Date().toISOString(),
      });
      setErrorMessage(t.saveResponseError);
      setStage(STAGES.ERROR);
    }
  };

  // Format date for display
  const formatAppointmentDate = () => {
    if (!linkData?.link?.appointmentDate) return "";
    const date = new Date(linkData.link.appointmentDate);
    return date.toLocaleDateString(getConfirmLocale(language), {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Generate Google Calendar link
  const generateCalendarLink = () => {
    if (!linkData?.link) return "";

    const startDate = new Date(linkData.link.appointmentDate);
    const [hours, minutes] = linkData.link.appointmentTime.split(":");
    startDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    const endDate = new Date(startDate);
    endDate.setMinutes(
      endDate.getMinutes() + (linkData.link.appointmentDuration || 45)
    );

    const formatDate = (date) => {
      return date
        .toISOString()
        .replace(/[-:]/g, "")
        .replace(/\.\d{3}/, "");
    };

    const title = encodeURIComponent(
      t.calendarTitle(linkData.clinic?.name || t.calendarFallbackClinic)
    );
    const details = encodeURIComponent(
      t.calendarDetails(linkData.clinic?.name || t.calendarFallbackClinicWith)
    );

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatDate(
      startDate
    )}/${formatDate(endDate)}&details=${details}`;
  };

  // Handle Waze navigation
  const handleWazeNavigation = () => {
    if (!linkData?.clinic?.address) return;

    const address = encodeURIComponent(linkData.clinic.address);

    // Check if on mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      // Try to open Waze app first, fallback to web
      const wazeAppUrl = `waze://?q=${address}&navigate=yes`;
      const wazeWebUrl = `https://waze.com/ul?q=${address}&navigate=yes`;

      // Create a hidden iframe to try the app scheme
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = wazeAppUrl;
      document.body.appendChild(iframe);

      // If app doesn't open within 2 seconds, open web version
      setTimeout(() => {
        document.body.removeChild(iframe);
        // Check if page is still visible (app didn't open)
        if (!document.hidden) {
          window.location.href = wazeWebUrl;
        }
      }, 2000);
    } else {
      // On desktop, open Waze web
      window.open(`https://waze.com/ul?q=${address}&navigate=yes`, "_blank");
    }
  };

  // Clinic hero header (shared across all public pages) — presentation only
  const renderClinicHeader = (compact = false) => (
    <PublicClinicHeader
      clinic={linkData?.clinic}
      eyebrow={t.pageEyebrow}
      compact={compact}
    />
  );

  // Render loading state
  if (stage === STAGES.LOADING) {
    return (
      <div {...pageProps}>
        <div className="capg-card capg-card-center capg-loading">
          <ContentLoader message={t.loading} />
        </div>
      </div>
    );
  }

  // Render error state
  if (stage === STAGES.ERROR) {
    return (
      <div {...pageProps}>
        <div className="capg-card capg-card-center capg-error">
          <div className="capg-status-circle capg-status-warning">
            <Warning size={36} weight="fill" />
          </div>
          <h1 className="capg-error-title">{t.errorTitle}</h1>
          <p className="capg-error-message">{errorMessage}</p>
        </div>
      </div>
    );
  }

  // Render verification stage
  if (stage === STAGES.VERIFY) {
    return (
      <div {...pageProps}>
        <div className="capg-card">
          {renderClinicHeader()}

          {/* Verification Form */}
          <div className="capg-verify">
            <div className="capg-status-circle capg-status-primary">
              <ShieldCheck size={32} weight="fill" />
            </div>
            <h2 className="capg-verify-title">{t.verifyTitle}</h2>
            <p className="capg-verify-subtitle">{t.verifySubtitle}</p>

            <div className="capg-code-wrap" dir="ltr">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setVerificationCode(value);
                }}
                placeholder="••••"
                className={`capg-code-input ${
                  verificationError ? "capg-code-input-error" : ""
                }`}
                autoFocus
                aria-label={t.verifyInputLabel}
              />
            </div>

            {verificationError && (
              <p className="capg-verify-error" role="alert">
                <Warning size={14} weight="fill" />
                <span>{verificationError}</span>
              </p>
            )}

            <button
              className="capg-btn capg-btn-primary capg-verify-btn"
              onClick={handleVerify}
              disabled={verificationCode.length !== 4}
            >
              {t.verifyContinue}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render confirming state
  if (stage === STAGES.CONFIRMING) {
    return (
      <div {...pageProps}>
        <div className="capg-card capg-card-center capg-loading">
          <ContentLoader message={t.saving} />
        </div>
      </div>
    );
  }

  // Render success state
  if (stage === STAGES.SUCCESS) {
    const isConfirmed = response === "confirmed";

    return (
      <div {...pageProps}>
        <div className="capg-card capg-card-center">
          {renderClinicHeader(true)}

          <div
            className={`capg-status-circle capg-status-pop ${
              isConfirmed ? "capg-status-success" : "capg-status-neutral"
            }`}
          >
            {isConfirmed ? (
              <CheckCircle size={44} weight="fill" />
            ) : (
              <XCircle size={44} weight="fill" />
            )}
          </div>

          <h2 className="capg-success-title">
            {isConfirmed ? t.successConfirmedTitle : t.successDeclinedTitle}
          </h2>

          <p className="capg-success-message">
            {isConfirmed
              ? t.successConfirmedMessage
              : t.successDeclinedMessage}
          </p>

          {/* Appointment Summary */}
          <div className="capg-summary">
            <div className="capg-summary-chip">
              <CalendarBlank size={14} weight="fill" />
              <span>{formatAppointmentDate()}</span>
            </div>
            <div className="capg-summary-chip">
              <Clock size={14} weight="fill" />
              <span>{linkData?.link?.appointmentTime}</span>
            </div>
          </div>

          {/* Action Buttons Container */}
          {isConfirmed && (
            <div className="capg-success-actions">
              <a
                href={generateCalendarLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="capg-btn capg-btn-outline"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>{t.addToCalendar}</span>
              </a>

              {linkData?.clinic?.address && (
                <button
                  onClick={handleWazeNavigation}
                  className="capg-btn capg-btn-outline"
                >
                  <img src={wazeIcon} alt="" className="capg-waze-icon" />
                  <span>{t.navigateWithWaze}</span>
                </button>
              )}
            </div>
          )}

          {/* Change Response */}
          <button
            className="capg-link-btn"
            onClick={() => setStage(STAGES.DETAILS)}
          >
            {t.changeResponse}
          </button>
        </div>
      </div>
    );
  }

  // Render details stage (main confirmation screen)
  return (
    <div {...pageProps}>
      <div className="capg-card">
        {renderClinicHeader()}

        {/* Greeting */}
        <div className="capg-greeting">
          <p className="capg-greeting-hello">
            {t.greetingHello}{" "}
            <strong>{linkData?.link?.patientName}</strong>,
          </p>
          <p className="capg-greeting-sub">{t.greetingSub}</p>
        </div>

        {/* Appointment Details */}
        <div className="capg-details">
          <h3 className="capg-details-title">{t.detailsTitle}</h3>

          <div className="capg-detail-row">
            <div className="capg-detail-icon">
              <CalendarBlank size={16} weight="fill" />
            </div>
            <div className="capg-detail-content">
              <span className="capg-detail-label">{t.labelDate}</span>
              <span className="capg-detail-value">
                {formatAppointmentDate()}
              </span>
            </div>
          </div>

          <div className="capg-detail-row">
            <div className="capg-detail-icon">
              <Clock size={16} weight="fill" />
            </div>
            <div className="capg-detail-content">
              <span className="capg-detail-label">{t.labelTime}</span>
              <span className="capg-detail-value">
                {linkData?.link?.appointmentTime}
              </span>
            </div>
          </div>

          {linkData?.link?.appointmentDuration && (
            <div className="capg-detail-row">
              <div className="capg-detail-icon">
                <Timer size={16} weight="fill" />
              </div>
              <div className="capg-detail-content">
                <span className="capg-detail-label">{t.labelDuration}</span>
                <span className="capg-detail-value">
                  {linkData.link.appointmentDuration} {t.durationUnit}
                </span>
              </div>
            </div>
          )}

          {linkData?.appointment?.videoMeetLink && (
            <div className="capg-detail-row">
              <div className="capg-detail-icon">
                <VideoCamera size={16} weight="fill" />
              </div>
              <div className="capg-detail-content">
                <span className="capg-detail-label">
                  {t.labelVideoMeeting}
                </span>
                <span className="capg-detail-value">{t.videoMeetingHint}</span>
              </div>
              <a
                href={linkData.appointment.videoMeetLink}
                className="capg-waze-chip"
                aria-label={t.videoJoinAria}
              >
                <VideoCamera size={14} weight="fill" />
                <span>{t.videoJoin}</span>
              </a>
            </div>
          )}

          {linkData?.clinic?.address && !linkData?.appointment?.videoMeetLink && (
            <div className="capg-detail-row">
              <div className="capg-detail-icon">
                <MapPin size={16} weight="fill" />
              </div>
              <div className="capg-detail-content">
                <span className="capg-detail-label">{t.labelAddress}</span>
                <span className="capg-detail-value">
                  {linkData.clinic.address}
                </span>
              </div>
              <button
                onClick={handleWazeNavigation}
                className="capg-waze-chip"
                aria-label={t.navigateAria}
              >
                <img src={wazeIcon} alt="" className="capg-waze-icon" />
                <span>{t.navigate}</span>
              </button>
            </div>
          )}

          {linkData?.clinic?.phone && (
            <div className="capg-detail-row">
              <div className="capg-detail-icon">
                <Phone size={16} weight="fill" />
              </div>
              <div className="capg-detail-content">
                <span className="capg-detail-label">{t.labelPhone}</span>
                <span className="capg-detail-value" dir="ltr">
                  {linkData.clinic.phone}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="capg-actions">
          <button
            className="capg-btn capg-btn-primary capg-btn-confirm"
            onClick={() => handleResponse("confirm")}
          >
            <CalendarCheck size={20} weight="bold" />
            <span>{t.confirmAttendance}</span>
          </button>

          {cancellationBlocked ? (
            <div className="capg-blocked-notice">
              <div className="capg-blocked-header">
                <Warning size={18} weight="fill" />
                <p>{t.blockedTitle}</p>
              </div>
              <p className="capg-blocked-sub">{t.blockedSub}</p>
              {linkData?.clinic?.phone && (
                <a
                  href={`tel:${linkData.clinic.phone}`}
                  className="capg-btn capg-btn-outline capg-blocked-call"
                >
                  <Phone size={16} weight="bold" />
                  <span>{t.callClinic}</span>
                </a>
              )}
            </div>
          ) : (
            <button
              className="capg-btn capg-btn-decline"
              onClick={() => handleResponse("decline")}
            >
              <CalendarX size={18} weight="bold" />
              <span>{t.cannotAttend}</span>
            </button>
          )}
        </div>
      </div>

      {/* Confirmation Popup */}
      {showConfirmPopup && (
        <div
          className="capg-popup-overlay"
          onClick={() => setShowConfirmPopup(false)}
        >
          <div
            className="capg-popup"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="capg-popup-handle" />
            <div
              className={`capg-status-circle ${
                confirmationType === "confirm"
                  ? "capg-status-success"
                  : "capg-status-danger"
              }`}
            >
              {confirmationType === "confirm" ? (
                <CalendarCheck size={30} weight="fill" />
              ) : (
                <CalendarX size={30} weight="fill" />
              )}
            </div>

            <h3 className="capg-popup-title">
              {confirmationType === "confirm"
                ? t.popupConfirmTitle
                : t.popupDeclineTitle}
            </h3>

            <p className="capg-popup-text">
              {confirmationType === "confirm"
                ? t.popupConfirmText
                : t.popupDeclineText}
            </p>

            <div className="capg-popup-buttons">
              <button
                className="capg-btn capg-popup-cancel"
                onClick={() => setShowConfirmPopup(false)}
              >
                {t.popupCancel}
              </button>
              <button
                className={`capg-btn ${
                  confirmationType === "confirm"
                    ? "capg-btn-primary"
                    : "capg-popup-danger"
                }`}
                onClick={submitResponse}
              >
                {confirmationType === "confirm"
                  ? t.popupConfirmAction
                  : t.popupDeclineAction}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConfirmAppointmentPage;
